/**
 * The six core schools of magic in Nimble (Core Rules 3.0, "Magic"): Fire, Ice, Lightning, Necrotic, Radiant and Wind. Each has its own cantrips and
 * nine Tiers of spells, and a spell deals the damage type of its school. This file holds how a school looks while a hero channels a spell: what the
 * orb of energy in front of the caster looks like and how the hands work it. Presentation only: the server knows a cast by its ability id and its
 * length, never by these numbers.
 *
 * These are not the schools of other games (Abjuration, Evocation, Illusion...). The rules also mention Secret Spells, whole schools "hidden or lost
 * through the ages" that adventurers may find; they have no entry here until the game has one.
 *
 * A spell belongs to a school through `schoolOfAbility`. `channel-<school>` is the generic channeling of a school (what scenarios and previews cast);
 * a real spell lists its own ability id in `abilitySchools` when it exists.
 */
export type SchoolId = 'fire' | 'ice' | 'lightning' | 'necrotic' | 'radiant' | 'wind';

/** How the small lights around the orb move. */
export type MoteMode =
  | 'orbit'   // circling the orb in tilted rings
  | 'spiral'  // winding in along a tight vortex
  | 'rise'    // lifted up from the ground, through the orb
  | 'fall'    // drifting down through the air around the orb
  | 'burst'   // thrown out in sparks and pulled back
  | 'stars'   // a steady crown of small bright points
  | 'drift';  // slow ribbons wandering around the orb

/** The look of a hand-working: the hands move around the orb on these paths, and move faster the nearer the spell is to completion. */
export interface HandStyle {
  /** Main strokes per second at the start; the tempo climbs to roughly two and a half times this by the end. */
  tempo: number;
  /** Metres the hands sweep around the orb in circles, and metres they squeeze in and out of it. */
  circle: number; knead: number;
  /** Metres of quick random-looking flicking on top, and how much the fingers curl (0 open, 1 clawed). */
  flick: number; claw: number;
  /** Radians the palms tip toward the orb (positive) or away from it. */
  tilt: number;
}

export interface SpellSchool {
  id: SchoolId; name: string;
  /** What an observer should see, in a sentence: the thing to compare the orb against. */
  flavor: string;
  /** Colours as 0xRRGGBB: the white-hot heart, the glow around it, and the accent of rings and motes. */
  palette: { core: number; glow: number; accent: number };
  orb: {
    /** Metres, at its fullest. */
    radius: number;
    /** `smooth` a plain shell of plasma; `lattice` a turning wire shell; `fluid` a body that wobbles and changes shape. */
    body: 'smooth' | 'lattice' | 'fluid';
    /** Tilted rings around the orb and how fast they turn (radians per second). */
    rings: number; ringSpeed: number;
    /** Share of the radius the surface wobbles by, and how hard the whole orb flickers (0 steady). */
    wobble: number; flicker: number;
    /** Share (0 to 1) of a black core eating the light from the inside; absent for none. */
    dark?: number;
    /** Jagged bolts that crackle out from the orb and are redrawn many times a second; absent for none. */
    arcs?: number;
    /** How fast the orb spins like a pulsar (1 is the usual, about a turn a second at the start, climbing to about three), and how long the twin beams that sweep round with it are (1 usual, 0 none). */
    spin?: number; beams?: number;
    /** Share of the usual light in the heart, glare, haloes, rings and flashes (1 usual, absent the same): low for an orb of darkness. */
    light?: number;
  };
  /** Sparks and similar that leave the orb and fall to the ground: thrown out at `speed` m/s, pulled down by `gravity` (m/s²), lasting `life` seconds, bouncing back `bounce` of their speed (0 lands dead). `swirl` carries them round the orb as they fall. */
  drops: { count: number; speed: number; gravity: number; life: number; size: number; bounce: number; swirl?: number };
  motes: { mode: MoteMode; count: number; size: number };
  hands: HandStyle;
}

export const schools: readonly SpellSchool[] = [
  { id: 'fire', name: 'Fire',
    flavor: 'A roaring orange orb with a white-hot heart, its surface licking and rolling, throwing sparks that arc out and are pulled back; fierce and flickering, the hands whip about it.',
    palette: { core: 0xfffbe6, glow: 0xff7a1f, accent: 0xffc247 },
    orb: { radius: .26, body: 'fluid', rings: 2, ringSpeed: 4.5, wobble: .16, flicker: .45, spin: 1.2, beams: 1 },
    motes: { mode: 'burst', count: 56, size: .049 },
    drops: { count: 56, speed: 1.7, gravity: 7, life: 1.4, size: .05, bounce: .35 },
    hands: { tempo: 1.9, circle: .08, knead: .1, flick: .03, claw: .5, tilt: 0 } },
  { id: 'ice', name: 'Ice',
    flavor: 'A pale blue crystal globe held in a slowly turning cage of facets, with frost rings settling into place and snow drifting down around it; precise and deliberate, the hands cup it.',
    palette: { core: 0xffffff, glow: 0x7fd4ff, accent: 0xe6fbff },
    orb: { radius: .23, body: 'lattice', rings: 2, ringSpeed: .9, wobble: 0, flicker: .03, spin: .7, beams: 1.1 },
    motes: { mode: 'fall', count: 40, size: .056 },
    drops: { count: 31, speed: 0.8, gravity: 8, life: 1.2, size: .055, bounce: .12 },
    hands: { tempo: .7, circle: .05, knead: .06, flick: .004, claw: 0, tilt: .25 } },
  { id: 'lightning', name: 'Lightning',
    flavor: 'A tight violet-blue ball of charge that never keeps still, jagged bolts cracking out of it and vanishing in a blink, sparks leaping around it; the hands twitch and snatch.',
    palette: { core: 0xf4f6ff, glow: 0x6a7bff, accent: 0xfff2a0 },
    orb: { radius: .22, body: 'smooth', rings: 1, ringSpeed: 6, wobble: .06, flicker: .4, arcs: 6, spin: 1.8, beams: .9 },
    motes: { mode: 'orbit', count: 26, size: .042 },
    drops: { count: 42, speed: 2.9, gravity: 3, life: .7, size: .045, bounce: .55 },
    hands: { tempo: 1.6, circle: .06, knead: .05, flick: .035, claw: .3, tilt: -.1 } },
  { id: 'necrotic', name: 'Necrotic',
    flavor: 'A sickly green light eaten from the inside by a black core, its surface crawling, with wisps lifted up out of the ground into it; the hands are clawed and grasping.',
    palette: { core: 0xd9ffd2, glow: 0x4a9a3a, accent: 0x9fe08a },
    orb: { radius: .23, body: 'fluid', rings: 1, ringSpeed: -.8, wobble: .18, flicker: .12, dark: .85, spin: .8, beams: .7 },
    motes: { mode: 'rise', count: 64, size: .063 },
    drops: { count: 28, speed: 0.3, gravity: 4, life: 1.6, size: .065, bounce: 0 },
    hands: { tempo: 1, circle: .07, knead: .12, flick: .012, claw: 1, tilt: .1 } },
  { id: 'radiant', name: 'Radiant',
    flavor: 'A steady golden-white sun that swells and settles like a breath, under a crown of small bright stars and slow haloes; unhurried and open, the hands trace wide, gentle circles.',
    palette: { core: 0xfffff0, glow: 0xffd666, accent: 0xfff3c4 },
    orb: { radius: .23, body: 'smooth', rings: 3, ringSpeed: .5, wobble: .03, flicker: .08, spin: .8, beams: 1.7 },
    motes: { mode: 'stars', count: 18, size: .063 },
    drops: { count: 39, speed: 0.8, gravity: 1.6, life: 2.2, size: .055, bounce: 0 },
    hands: { tempo: .6, circle: .12, knead: .03, flick: .003, claw: 0, tilt: .1 } },
  { id: 'wind', name: 'Wind',
    flavor: 'A pale mint cyclone: a thin, fast-turning shell wound with streaks of air, lights spiralling in around it and tilted rings spinning like a gyroscope; the hands sweep in wide, quick arcs.',
    palette: { core: 0xffffff, glow: 0xa8f0c4, accent: 0xf0fff4 },
    orb: { radius: .23, body: 'smooth', rings: 3, ringSpeed: 2.8, wobble: .05, flicker: .1, spin: 1.5, beams: .9 },
    motes: { mode: 'spiral', count: 64, size: .056 },
    drops: { count: 31, speed: 1.2, gravity: 1, life: 1.6, size: .045, bounce: .2, swirl: 2.2 },
    hands: { tempo: 1.2, circle: .14, knead: .04, flick: .01, claw: .1, tilt: -.15 } },
];

export function findSchool(id: string): SpellSchool | undefined { return schools.find(school => school.id === id); }

/**
 * Spells by ability id. The Wind cantrips give only their colour (they are not channeled, see `CANTRIP_ACTIONS`); Vicious Mockery is Songweaver-only
 * and is not a Wind cantrip. The Shadowmancer's cantrips take `shadowChannel`.
 */
const abilitySchools: Readonly<Record<string, SchoolId>> = { 'razor-wind': 'wind', 'breath-of-life': 'wind',
  // The Mage's Elemental Spellcasting.
  'flame-dart': 'fire', 'hearts-fire': 'fire', 'ice-lance': 'ice', snowblind: 'ice', zap: 'lightning', overload: 'lightning' };
const SHADOWMANCER_CANTRIPS: ReadonlySet<string> = new Set(['shadow-blast', 'summon-shadow', 'command-shadows']);

/** The ability id of a generic channeling in a school. */
export const channelAbility = (school: SchoolId) => `channel-${school}`;

/** The school an ability channels, or undefined when it is not a spell (a sword hero's power, say). */
export function schoolOfAbility(abilityId: string): SpellSchool | undefined {
  // Mockery's colour (the targeting cursor); it is a cantrip with its own jeer, not a seventh school or a Wind cantrip.
  if (abilityId === 'vicious-mockery') return mockeryChannel;
  if (SHADOWMANCER_CANTRIPS.has(abilityId)) return shadowChannel;
  const id = abilitySchools[abilityId] ?? (abilityId.startsWith('channel-') ? abilityId.slice('channel-'.length) : undefined);
  return id === undefined ? undefined : findSchool(id);
}

const windChannel = findSchool('wind')!;
const mockeryChannel: SpellSchool = {
  ...windChannel, name: 'Vicious Mockery',
  palette: { core: 0xffeffa, glow: 0xbc80e9, accent: 0xffd2ed },
  orb: { ...windChannel.orb, body: 'fluid', rings: 2, wobble: .065, beams: .45 },
  motes: { mode: 'spiral', count: 20, size: .04 },
  drops: { ...windChannel.drops, count: 8 },
};
const necroticChannel = findSchool('necrotic')!;
/**
 * The Shadowmancer's cantrips are Necrotic by the rules, so their cast reticle is the school's. They are not channeled (no orb, chant or
 * school voice): each has its own clawed clip and dark smoke (`CANTRIP_ACTIONS`, `shadow-charges.ts`). The patron's darkness, not a seventh school.
 */
const shadowChannel: SpellSchool = { ...necroticChannel, name: 'Shadow' };
